
// Pure geometric core for the two range-level Z-profile operations:
// "Interpolér Z mellem punkter" and "Sæt fald/hældning" (see
// VectorEditingMenu.js for the interactive wiring). No DOM/THREE dependency,
// plain {x,y,z} points, Level 1 (no-browser) unit testable.
//
//   Z-profile (geometry core)
//           |
//           +-- horizontalStation()       - the shared "distance" definition
//           +-- interpolateZBetween()     - section: linear Z between two fixed endpoints
//           +-- setSlopeBetween()         - section: Z from a reference end + a target slope
//
// ── Station definition (shared by both operations) ──────────────────────
// Both operations need a notion of "how far along the line is vertex i", to
// turn a target Z-profile (linear between two Z values, or a constant
// slope) into a per-vertex Z. Task requirement (§12/§13): this must be the
// cumulative HORIZONTAL (planimetric/XY) distance along the range's CURRENT
// path - NOT vertex index (arbitrary spacing), and NOT cumulative 3D
// distance (which is circular for §13: a slope is rise/HORIZONTAL-run, so
// using 3D length - which itself depends on the Z the slope is about to
// compute - would be self-referential). This matches standard ledningsdrift/
// vejprofil practice (station = chainage along the plan alignment) and is
// the one place both operations get their "afstand" from - never two
// slightly different definitions.
function horizontalDist(a, b){
	return Math.hypot(b.x - a.x, b.y - a.y);
}

// Cumulative horizontal (XY) distance from points[startIndex] to every
// vertex up to and including points[endIndex], along the CURRENT path (not
// a straight chord - matches distributePointsEvenly()'s own "along the
// existing route" principle, just measured in the XY plane instead of 3D).
// Returns {stations, total} - stations[i] is the distance from startIndex to
// vertex (startIndex+i); stations[0] === 0.
function horizontalStations(points, startIndex, endIndex){
	const stations = [0];
	let acc = 0;
	for(let i = startIndex + 1; i <= endIndex; i++){
		acc += horizontalDist(points[i - 1], points[i]);
		stations.push(acc);
	}
	return {stations, total: acc};
}

function clonePoint(p){ return {x: p.x, y: p.y, z: p.z}; }

// ── Interpolér Z mellem punkter ──────────────────────────────────────────
//
// The two range endpoints (`startIndex`/`endIndex`) are FIXED (task: "De to
// rand-vertex fastholdes"). Every interior vertex keeps its own X/Y
// UNCHANGED and gets a new Z linearly interpolated between the two
// endpoints' Z, by its fraction of the TOTAL horizontal station (see this
// file's header comment) - NOT by vertex index, so unevenly-spaced vertices
// still get a physically-consistent Z-profile.
//
// A zero-length horizontal span (the range is a vertical run in plan, or has
// only 2 vertices) has no meaningful station fraction - every interior
// vertex is left untouched in that case rather than dividing by zero.
export function interpolateZBetween(points, startIndex, endIndex){
	const result = points.map(clonePoint);
	if(endIndex - startIndex < 2) return {points: result, maxDeviation: 0}; // no interior vertex

	const z0 = points[startIndex].z, zN = points[endIndex].z;
	const {stations, total} = horizontalStations(points, startIndex, endIndex);
	if(total < 1e-9) return {points: result, maxDeviation: 0};

	let maxDeviation = 0;
	for(let i = startIndex + 1; i < endIndex; i++){
		const frac = stations[i - startIndex] / total;
		const newZ = z0 + frac * (zN - z0);
		maxDeviation = Math.max(maxDeviation, Math.abs(newZ - points[i].z));
		result[i] = {x: points[i].x, y: points[i].y, z: newZ};
	}
	return {points: result, maxDeviation};
}

// ── Sæt fald/hældning ─────────────────────────────────────────────────────
//
// ONE endpoint is the kote-reference (`referenceEnd`: "start" | "end") and
// keeps its Z exactly as-is; EVERY OTHER vertex in the range - every interior
// vertex AND the FAR endpoint - gets a new Z computed from the reference
// kote, the given slope, and its own horizontal station (task §13: "Z for de
// øvrige vertices beregnes fra: referencekoten, den angivne hældning,
// relevant station" - "de øvrige" is every vertex except the reference one,
// unlike interpolateZBetween() above where BOTH endpoints are fixed and only
// the interior is touched. This is the deliberate, documented semantic
// difference between the two operations).
//
// `slope` is a signed ratio (rise/horizontal-run) measured in a FIXED
// direction - increasing station, i.e. from startIndex toward endIndex -
// exactly like interpolateZBetween()'s station. `referenceEnd` only picks
// WHICH endpoint's Z is the fixed input; it deliberately does NOT flip the
// physical meaning of the sign, so switching the reference end alone never
// silently reverses an uphill run into a downhill one - a real direction
// reversal is a separate, explicit "vend retning" action (negate `slope`) in
// the caller's UI. This matches standard ledningsdrift/vejprofil practice,
// where a fald/stigning is a property of the alignment's own direction, not
// of which end happens to be typed in as the known kote.
//
// newZ(i) = zRef + slope * (station(i) - station(ref)) - both stations
// measured from the SAME fixed origin (startIndex), so this reduces to
// ordinary linear extrapolation from the reference point in either
// direction. X/Y are never changed.
export function setSlopeBetween(points, startIndex, endIndex, {referenceEnd = "start", slope = 0} = {}){
	const result = points.map(clonePoint);
	if(endIndex - startIndex < 1) return {points: result, maxDeviation: 0};

	const refIndex = referenceEnd === "end" ? endIndex : startIndex;
	const zRef = points[refIndex].z;

	const {stations} = horizontalStations(points, startIndex, endIndex);
	const stationOf = (i) => stations[i - startIndex];
	const stationRef = stationOf(refIndex);

	let maxDeviation = 0;
	for(let i = startIndex; i <= endIndex; i++){
		if(i === refIndex) continue;
		const newZ = zRef + slope * (stationOf(i) - stationRef);
		maxDeviation = Math.max(maxDeviation, Math.abs(newZ - points[i].z));
		result[i] = {x: points[i].x, y: points[i].y, z: newZ};
	}
	return {points: result, maxDeviation};
}
